import { PopoverContentProps } from '@radix-ui/react-popover';
import { AlertTriangle } from 'lucide-react';
import { forwardRef, type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { useControllableState } from 'v5/platform/react/useControllableState';
import { Button, type ButtonProps } from 'v5/design-system/Button';
import {
  defaultColorLibrary,
  findColorLibraryItem,
  type ColorPickerLibrary
} from './ColorPicker.library';
import {
  ColorPickerGridGallery,
  usePickerGridFocusRef
} from './ColorPickerGridGallery';
import {
  ColorSwatchPreview,
  useColorPickerDismissFix
} from './ColorPicker.shared';
import { ColorPickerHexField } from './ColorPickerHexField';
import { ColorPickerSpectrum } from './ColorPickerSpectrum';
import {
  resolveColorDisplay,
  resolveColorLibraryItemValue
} from './ColorPicker.utils';
import { DropdownMenuButtonArrow } from 'v5/design-system/DropdownMenu';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from 'v5/design-system/Popover';

export type ColorPickerProps = {
  value: string;
  onChange: (value: string) => void;
  library?: ColorPickerLibrary;
  /** Any colour too: a saturation and brightness area and a hue slider above the swatches. */
  spectrum?: boolean;
  /** The colour that applies while there is no value: its swatch, faded, and where the spectrum and the hex field start. */
  placeholderColour?: string;
  /**
   * A warning about the value (an attention border, a triangle, the text in a tooltip); `null` for
   * none. A host whose warning comes and goes passes `null`, not nothing, so the field stays put.
   */
  warning?: string | null;
  /** What the value is of, read before the value itself. */
  'aria-labelledby'?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  className?: string;
};

/**
 * Color select: DS {@link Button} as Radix {@link PopoverTrigger} + swatch grid.
 * Enter / Space / focus restore are left to Radix.
 */
export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>(
  (
    {
      value,
      onChange,
      library = defaultColorLibrary,
      spectrum = false,
      placeholderColour,
      warning,
      'aria-labelledby': labelledBy,
      disabled,
      readOnly,
      placeholder,
      align = 'start',
      side,
      open: openProp,
      onOpenChange,
      variant = 'secondary',
      size = 'sm',
      className
    },
    ref
  ) => {
    const { t } = useTranslation('patterns', { keyPrefix: 'ColorPicker' });
    const resolvedPlaceholder = placeholder ?? t('placeholder');
    const [open, setOpen] = useControllableState({
      prop: openProp,
      onChange: onOpenChange,
      fallbackValue: false
    });
    const { ref: colorGridRef, focusGrid } = usePickerGridFocusRef();
    const labelId = useId();
    const selectedId = findColorLibraryItem(library, value)?.id ?? '';
    const display = resolveColorDisplay(library, value);
    const shown =
      value === '' && placeholderColour !== undefined
        ? placeholderColour
        : value;
    const isInteractive = !disabled && !readOnly;
    const { rootProps, popoverProps } = useColorPickerDismissFix(open, setOpen);
    const warningId = useId();
    const hasWarning = typeof warning === 'string';

    return (
      <Popover
        open={isInteractive && open}
        onOpenChange={next => {
          if (!isInteractive) return;
          setOpen(next);
        }}
      >
        <WarningTooltip warning={warning}>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              ref={ref}
              type="button"
              variant={variant}
              size={size}
              disabled={disabled}
              aria-readonly={readOnly || undefined}
              tabIndex={readOnly ? -1 : undefined}
              aria-labelledby={
                labelledBy === undefined ? labelId : `${labelledBy} ${labelId}`
              }
              {...(hasWarning && { 'aria-describedby': warningId })}
              className={cn(
                'w-full justify-between font-normal',
                readOnly && 'pointer-events-none cursor-default',
                hasWarning &&
                  'border-border-attention-emphasis not-aria-readonly:not-aria-disabled:hocus:border-border-attention-emphasis',
                className
              )}
              {...rootProps}
              onKeyDown={e => {
                if (isInteractive && !open && e.key === 'ArrowDown') {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <ColorSwatchPreview
                  hex={display.hex ?? (shown === value ? undefined : shown)}
                  render={display.swatchRender}
                  className={cn(shown !== value && 'opacity-60')}
                />
                <span
                  id={labelId}
                  className={cn(
                    'min-w-0 truncate text-left',
                    display.name ? 'text-fg-default' : 'text-fg-muted'
                  )}
                >
                  {display.name ?? resolvedPlaceholder}
                </span>
              </span>
              {hasWarning && (
                <>
                  <AlertTriangle
                    aria-hidden
                    className="shrink-0 text-fg-attention"
                  />
                  <span id={warningId} hidden>
                    {warning}
                  </span>
                </>
              )}
              {isInteractive ? (
                <DropdownMenuButtonArrow className="shrink-0 opacity-50" />
              ) : null}
            </Button>
          </PopoverTrigger>
        </WarningTooltip>

        <PopoverContent
          align={align}
          side={side}
          className="w-80 p-3"
          {...popoverProps}
          onOpenAutoFocus={e => {
            e.preventDefault();
            focusGrid();
          }}
        >
          <div className="flex flex-col gap-3">
            {spectrum && (
              <ColorPickerSpectrum
                value={shown}
                onChange={onChange}
                disabled={!isInteractive}
              />
            )}
            <ColorPickerGridGallery
              gridRef={colorGridRef}
              ariaLabel={resolvedPlaceholder}
              colors={library.colors}
              selectedId={selectedId}
              onSelectedIdChange={nextId => {
                const item = library.colors.find(color => color.id === nextId);
                if (item) {
                  onChange(resolveColorLibraryItemValue(item));
                  setOpen(false);
                }
              }}
              onClose={() => setOpen(false)}
            />
            <ColorPickerHexField
              value={shown}
              disabled={!isInteractive}
              onCommit={hex => onChange(hex)}
              onSubmit={() => setOpen(false)}
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';

/** The warning's tooltip over the trigger, only for a host that passes one (or `null`). */
function WarningTooltip({
  warning,
  children
}: {
  warning: string | null | undefined;
  children: ReactElement;
}) {
  if (warning === undefined) return children;
  return (
    <SimpleTooltip title={warning ?? ''} delayDuration={0}>
      {children}
    </SimpleTooltip>
  );
}

export default ColorPicker;
